/**
 * Student display names — SRS B3.
 *
 * 2–30 characters, profanity-filtered. This is the ONLY thing the system ever learns
 * about a student (SRS L2), so the rules live here and are shared by the join screen
 * and the server, which checks again because the client is never trusted.
 */
export const NAME_MIN_LENGTH = 2;
export const NAME_MAX_LENGTH = 30;
/**
 * Words a student may not use as a name.
 *
 * Deliberately short and matched whole-word, so real names are never rejected —
 * a child blocked from typing their own name is a worse failure than a rude one
 * slipping through, and the teacher can rename anyone from the participants panel.
 */
const BLOCKED_WORDS = [
    'anus', 'arse', 'arsehole', 'ass', 'asshole', 'bastard', 'bitch', 'bollocks',
    'boner', 'boob', 'boobs', 'bugger', 'clit', 'cock', 'coon', 'crap', 'cum',
    'cunt', 'dick', 'dickhead', 'dildo', 'douche', 'dyke', 'fag', 'faggot',
    'fuck', 'fucker', 'fucking', 'gash', 'jizz', 'knob', 'minge', 'motherfucker',
    'nigga', 'nigger', 'nonce', 'paki', 'penis', 'piss', 'poop', 'porn', 'prick',
    'pussy', 'queer', 'rape', 'rapist', 'retard', 'scrotum', 'sex', 'shag',
    'shit', 'slag', 'slut', 'spastic', 'spunk', 'tits', 'turd', 'twat', 'vagina',
    'wank', 'wanker', 'whore', 'willy',
];
const LEET = {
    '0': 'o', '1': 'i', '3': 'e', '4': 'a', '5': 's', '7': 't', '8': 'b',
    '@': 'a', '$': 's', '!': 'i', '+': 't',
};
/** Trims the ends and collapses runs of whitespace. */
export function normaliseStudentName(raw) {
    return raw.trim().replace(/\s+/g, ' ');
}
/** The dedupe key stored as `normalised_name` — lowercase, trimmed, single-spaced. */
export function studentNameKey(raw) {
    return normaliseStudentName(raw).toLowerCase();
}
/** Folds the substitutions people use to sneak a word past a filter: `sh1t` → `shit`. */
function unleet(value) {
    return value.replace(/[01345786@$!+]/g, (character) => LEET[character] ?? character);
}
/** True when any whole word of the name is on the blocked list. */
export function containsProfanity(name) {
    const words = unleet(name.toLowerCase())
        .split(/[^a-z]+/)
        .filter((word) => word !== '');
    return words.some((word) => BLOCKED_WORDS.includes(word));
}
/** Checks a typed name and, when it fails, says what to do about it in plain English. */
export function checkStudentName(raw) {
    const name = normaliseStudentName(raw);
    if (name === '')
        return { ok: false, problem: 'empty', message: 'Please type your first name.' };
    if (name.length < NAME_MIN_LENGTH) {
        return { ok: false, problem: 'too-short', message: 'Please use at least 2 letters.' };
    }
    if (name.length > NAME_MAX_LENGTH) {
        return { ok: false, problem: 'too-long', message: 'Please use 30 letters or fewer.' };
    }
    if (containsProfanity(name)) {
        return { ok: false, problem: 'not-allowed', message: 'Please use your real first name.' };
    }
    return { ok: true };
}
//# sourceMappingURL=student-name.js.map